<template>
  <div
    :key="`lookbook-product-grid-modal-${index}`"
    class="lookbook-product-grid-modal__container"
  >
    <div
      v-if="products.length"
      :key="blockConfigsTracker"
      class="lookbook-images__product-cards-wrapper"
    >
      <div class="lookbook-images__buttons-wrapper">
        <button
          v-if="blockConfigs[index]?.productSelected"
          class="lookbook-images__close-atc"
          type="button"
          show-icon
          @click="closeAtc(index)"
        >
          <icon-arrow-left />
          <span
            class="
                lookbook-images__close-atc-label
                text-body-2xs-regular
              "
            v-text="
              showBackInStock
                ? $string('lookbook.back_to_look')
                : $string('lookbook.return_to_look')
            "
          />
        </button>
        <button
          v-else
          class="lookbook-images__products-close"
          type="button"
          @click="closeModal"
        >
          <icon-close />
        </button>
      </div>
      <div
        class="lookbook-images__carousel-wrapper"
        :class="{ 'grid-wrapper': blockConfigs[index]?.productSelected }"
      >
        <splide
          :ref="`splide-${index}`"
          class="lookbook-images__carousel"
          :has-track="false"
          :options="getSplideConfigs(products.length, index)"
        >
          <splide-track>
            <splide-slide
              v-for="(product, productIndex) of products"
              :key="productIndex"
            >
              <product-card
                :product="product"
                card-layout="small"
                :show-wishlist="false"
                :enable-flat-image="false"
                enable-variant-image
                :show-badges="false"
                :show-swatches="false"
                :load-secondary-image="false"
                :show-product-content="false"
                :image-background-color="getImageBackgroundColor(blockConfigs[index])"
                :show-quick-add="
                  !blockConfigs[index]?.productSelected"
                enable-image-carousel
                :carousel-limit="4"
                custom-atc
                :product-card-location="location || {
                  key: 'sectionHotspot',
                }"
                @custom-quick-add="handleAddToCart(index, productIndex)"
              />
            </splide-slide>
          </splide-track>
          <div class="lookbook-images__arrows splide__arrows">
            <button
              class="splide__arrow splide__arrow--prev"
              type="button"
            >
              <span
                class="visually-hidden"
                v-text="$string('accessibility.carousel.previous')"
              />
              <icon-arrow-left />
            </button>

            <button
              class="splide__arrow splide__arrow--next"
              type="button"
            >
              <span
                class="visually-hidden"
                v-text="$string('accessibility.carousel.next')"
              />
              <icon-arrow-right />
            </button>
          </div>
        </splide>
        <div
          v-if="blockConfigs[index]?.productSelected"
          class="lookbook-images__product-details"
          :class="{
            'bis-active': showBackInStock
          }"
        >
          <quick-add-to-cart
            v-if="blockConfigs[index].viewedProduct"
            size-guide-position="top"
            :number-of-sizes-per-row="7"
            show-confirmation-button
            show-view-product-button
            :product="blockConfigs[index].viewedProduct"
            :show-back-in-stock="showBackInStock"
            show-product-details
            is-stl-type
            :product-card-location="location || {
              key: 'sectionHotspot',
            }"
            :bis-settings="{
              showCloseButton: false,
              namespace:
                `lookbook-${blockConfigs[index].viewedProduct.handle}`,
              marketingText: $string('product.back_in_stock.marketing_opt_in')
            }"
            @bis-active="handleBackInStockModal"
          />
        </div>
      </div>
    </div>
    <klaviyo-bis-message v-if="isMobile && showSuccessMessage" />
  </div>
</template>

<script>

/**
 * Vue: Product List (product-grid)
 * -----------------------------------------------------------------------------
 * Lookbook Mobile Product list modal.
 *
 */
import { Splide, SplideSlide, SplideTrack } from '@splidejs/vue-splide'
import { mapActions, mapState } from 'vuex'

import ProductCard from '~global/product-card/product-card'
import QuickAddToCart from '~async/quick-add-to-cart/quick-add-to-cart'

import IconArrowLeft from '~icons/directional-navigation/chevron/left.svg'
import IconArrowRight from '~icons/directional-navigation/chevron/right.svg'
import IconClose from '~icons/general/close.svg'
import KlaviyoBisMessage from '~async/klaviyo-bis/klaviyo-bis-message'

export default {
  name: 'LookbookProductGridModal',

  components: {
    IconArrowLeft,
    IconArrowRight,
    IconClose,
    ProductCard,
    QuickAddToCart,
    Splide,
    SplideSlide,
    SplideTrack,
    KlaviyoBisMessage,
  },

  data() {
    return {
      active: false,
      products: [],
      blockConfigs: [],
      blockConfigsTracker: 0,
      index: 0,
      splide: {},
      showBackInStock: false,
      showSuccessMessage: false,
    }
  },

  computed: {

    /**
     * Map Vuex state.
     */
    ...mapState({
      lastAddedProductId: (state) => state.quickAddToCart.lastAddedProductId,
      shopTheLookSelectedProducts: (state) => state.shopTheLook.selectedProducts,
      shopTheLookBlockConfigs: (state) => state.shopTheLook.blockConfigs,
      shopTheLookIndex: (state) => state.shopTheLook.index,
      stateSize: (state) => state.quickAddToCart.size,
      activeOverlay: (state) => state.overlays.active[0],
      location: (state) => state.shopTheLook.sectionLocation,
    }),

    /**
     * Compute success message.
     * @returns {String}
     */
    successMessage() {
      if (['os', 'one size'].includes(this.stateSize?.toLowerCase())) {
        return this.$string('product.cta.added_to_cart')
      }

      return this.$string('product.cta.success_added_size', {
        replace: { size: this.stateSize },
      })
    },
  },

  watch: {
    blockConfigs: {
      handler() {
        this.blockConfigsTracker++
      },
      deep: true,
    },

    lastAddedProductId() {
      if (this.lastAddedProductId) {
        this.closeAtc()
      }
    },

    shopTheLookSelectedProducts: {
      handler() {
        this.products = this.shopTheLookSelectedProducts
      },
      immediate: true,
    },

    shopTheLookIndex: {
      handler() {
        this.index = this.shopTheLookIndex
      },
      immediate: true,
    },

    shopTheLookBlockConfigs: {
      handler() {
        this.blockConfigs = this.shopTheLookBlockConfigs
      },
      immediate: true,
    },

    activeOverlay() {
      if (this.activeOverlay?.namespace === 'lookbook-product-grid-modal') {
        this.active = true
      } else if (this.activeOverlay?.namespace === 'sizeGuideDrawer' && this.active) {
        this.products = this.shopTheLookSelectedProducts
      } else if (this.activeOverlay?.namespace !== 'lookbook-product-grid-modal' && this.active) {
        this.active = true
      }
    },
  },

  methods: {

    /**
     * Map Vuex actions.
     */
    ...mapActions({
      openOverlay: 'overlays/open',
    }),

    /**
     * Get splide configs
     */
    getSplideConfigs(productCount, index) {
      const showPerView = productCount === 4 ? 4 : 3

      return {
        autoplay: false,
        type: 'slide',
        perPage: this.blockConfigs[index]?.productSelected ? 1 : showPerView,
        direction: 'ltr',
        drag: false,
        speed: this.$timing('normal'),
        start: this.blockConfigs[index]?.viewedProductIndex,
        pauseOnHover: false,
        arrows: false,
        pagination: false,
      }
    },

    getImageBackgroundColor(block) {
      return block.cardBackground
    },

    closeModal() {
      window.cnvs?.EventBus?.emit('overlay:close-product-grid')
    },

    closeAtc(blockIndex) {
      if (this.blockConfigs[blockIndex]?.currentSplide?.go) {
        this.blockConfigs[blockIndex].currentSplide.go(0)
      }

      if (this.blockConfigs[blockIndex]) {
        this.blockConfigs[blockIndex].productSelected = false
        this.blockConfigs[blockIndex].viewedProduct = {}
      }

      if (this.showBackInStock) {
        this.showBackInStock = false
      }
    },

    /**
     * Show single product and atc size grid.
     * @param {Object} product - Formatted product object.
     * @param {Number} index - Product forloop index.
     */
    handleAddToCart(blockIndex, productsIndex) {
      this.blockConfigs[blockIndex] = {
        ...this.blockConfigs[blockIndex],
        viewedProduct: this.blockConfigs[blockIndex].selectedProducts[productsIndex],
        productSelected: true,
        currentSplide: this.$refs[`splide-${blockIndex}`],
        viewedProductIndex: productsIndex,
      }

      this.blockConfigs[blockIndex].currentSplide.splide.event.on('resized', () => {
        this.blockConfigs[blockIndex].currentSplide
          .go(this.blockConfigs[blockIndex].viewedProductIndex)
      })

      this.blockConfigs[blockIndex].currentSplide.splide.event.on('moved', (newIndex) => {
        this.blockConfigs[blockIndex].viewedProductIndex = newIndex
        this.blockConfigs[blockIndex].viewedProduct =
          this.blockConfigs[blockIndex].selectedProducts[newIndex]
      })
    },

    /**
     * Handles back in stock modal display
     * @param {Boolean} isActive - back in stock display state
     */
    handleBackInStockModal(isActive) {
      this.showBackInStock = isActive
    },

    /**
     * Handle back in stock success message.
     */
    handleBISSuccess() {
      this.showBackInStock = false
      if (this.isMobile) {
        this.showSuccessMessage = true
        setTimeout(() => {
          this.showSuccessMessage = false
        }, this.$timing('message'))
      }

      this.openOverlay({
        ignoreDismissed: true,
        namespace: 'bis-success-modal',
      })
    },
  },

}

</script>

<style lang="scss">
@import './lookbook-product-grid-modal';
</style>
